import React, { useState, useContext, useEffect } from "react";
import AuthContext from "../context/AuthContext";
import TenantContext, { getApexHost, getSubdomain, TENANT_REDIRECT_DISABLED } from "../context/TenantContext";
import { useNavigate, useLocation, Link, useSearchParams } from "react-router-dom";
import { Mail, Lock, LogIn, AlertCircle, CheckCircle, Eye, EyeOff, ArrowRight, Shield } from "lucide-react";
import { motion } from "framer-motion";
import ssoService from "../services/ssoService";

const SSO_ERROR_MESSAGES = {
  invalid_state: "SSO session expired. Please try again.",
  server_error: "An error occurred during SSO authentication. Please try again.",
  sso_disabled: "SSO is not enabled for your organization.",
  auth_failed: "Authentication with identity provider failed. Please try again.",
  user_not_found: "No account found for your SSO email. Contact your administrator.",
  user_inactive: "Your account is inactive. Contact your administrator.",
  org_inactive: "Your organization has been deactivated. Contact your administrator.",
  wrong_org: "That account doesn't belong to this organization.",
  // Workvivo SAML SSO specific codes
  no_email: "Workvivo didn't share an email address for your account.",
  replay: "That sign-in link was already used. Please try again.",
  saml_signature: "Single sign-on couldn't be verified. Please try again.",
  saml_expired: "The sign-in request expired. Please try again.",
  saml_invalid: "Single sign-on configuration mismatch. Contact your administrator.",
  saml_unconfigured: "Workvivo sign-in isn't set up for your organization yet.",
  saml_idp_metadata: "Couldn't reach Workvivo's sign-in service. Please try again.",
};

const Login = () => {
  const { login, user, loading, isInitialized } = useContext(AuthContext);
  const { scopedOrgId, scopedTheme } = useContext(TenantContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [localLoading, setLocalLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState("email"); // "email" | "sso-redirect" | "password" | "mixed-choice" | "set-password"
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [ssoAuthUrl, setSsoAuthUrl] = useState(null);
  // Workvivo SAML SSO availability for the entered email's org (SP-initiated button).
  const [workvivoSso, setWorkvivoSso] = useState({ enabled: false, orgId: null });
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [settingPassword, setSettingPassword] = useState(false);

  // Get the intended destination from location state, or default to home
  const from = location.state?.from || "/";

  // Check for SSO error params
  useEffect(() => {
    const ssoError = searchParams.get("error");
    if (ssoError) {
      setError(SSO_ERROR_MESSAGES[ssoError] || "SSO authentication failed. Please try again.");
      // Clean error from URL
      window.history.replaceState({}, document.title, "/login");
    }
  }, [searchParams]);

  // Pre-fill email when arriving from a cross-host email-based redirect
  // (e.g., user typed their email on the apex; backend told us their org
  // has a subdomain so we hopped here with ?email=<addr>).
  useEffect(() => {
    const prefill = searchParams.get("email");
    if (prefill) {
      setEmail(prefill);
      window.history.replaceState({}, document.title, "/login");
    }
  }, [searchParams]);

  // Check for success message from password reset or signup
  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      // Clear the message from location state
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Redirect to intended destination if user is authenticated
  useEffect(() => {
    if (isInitialized && user) {
      navigate(from, { replace: true });
    }
  }, [user, navigate, from, isInitialized]);

  const handleCheckEmail = async (e) => {
    e.preventDefault();
    if (checkingEmail) return;

    setCheckingEmail(true);
    setError("");

    try {
      const result = await ssoService.checkEmail(email);

      // Workvivo SAML SSO is independent of the OIDC auth_mode — surface the
      // "Sign in with Workvivo" button whenever the org has it enabled.
      setWorkvivoSso({
        enabled: !!result.workvivo_sso_enabled,
        orgId: result.organization_id || null,
      });

      // If the email's org has a subdomain and we're not on it, hop to
      // the tenant subdomain so branding and the org-scope guard apply
      // before the user enters a password.
      if (!TENANT_REDIRECT_DISABLED && result.subdomain) {
        const currentSub = getSubdomain();
        if (result.subdomain !== currentSub) {
          const apex = getApexHost();
          const target =
            `https://${result.subdomain}.${apex}` +
            `/login?email=${encodeURIComponent(email)}`;
          window.location.replace(target);
          return; // Navigating away — don't reset state
        }
      }

      if (result.authorization_url) {
        setSsoAuthUrl(result.authorization_url);
      }
      if (result.must_change_password) {
        setMustChangePassword(true);
      }

      if (result.auth_method === "sso" && result.authorization_url) {
        // SSO user — redirect to identity provider
        setStep("sso-redirect");
        window.location.href = result.authorization_url;
        return; // Don't reset loading — we're navigating away
      } else if (result.auth_method === "mixed_choice") {
        // Mixed org, undetermined user — show both options
        setStep("mixed-choice");
      } else {
        // Password login or unknown
        setStep("password");
      }
    } catch {
      // check-email failed — fall back to password
      setStep("password");
    } finally {
      setCheckingEmail(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (loading || localLoading) return; // Prevent multiple submissions

    setLocalLoading(true);
    setError("");

    try {
      const result = await login(email, password, rememberMe, scopedOrgId);

      // If backend flagged a required password change, show set-password step
      if (result?.must_change_password) {
        setMustChangePassword(true);
        setStep("set-password");
        setLocalLoading(false);
        return;
      }
      // No need to navigate here - the useEffect will handle it
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || "Invalid email or password");
      setLocalLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setStep("email");
    setPassword("");
    setError("");
    setMustChangePassword(false);
    setNewPassword("");
    setConfirmPassword("");
  };

  const handleMixedChoiceSSO = () => {
    if (!ssoAuthUrl) return;
    setStep("sso-redirect");
    setError("");
    window.location.href = ssoAuthUrl;
  };

  // SP-initiated Workvivo SAML: full-page nav to the backend, which 302s to
  // Workvivo. Must be a real navigation (not XHR) to follow the auth redirect.
  const handleWorkvivoSso = () => {
    if (!workvivoSso.orgId) return;
    setError("");
    setStep("sso-redirect");
    const apiBase = process.env.REACT_APP_API_URL || "";
    window.location.assign(
      `${apiBase}/api/v1/auth/sso/saml/${workvivoSso.orgId}/login`
    );
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    if (settingPassword) return;

    setError("");
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    setSettingPassword(true);
    try {
      const result = await ssoService.setPassword(newPassword);
      const newToken = result?.data?.access_token || result?.access_token;
      if (newToken) {
        localStorage.setItem("token", newToken);
        sessionStorage.setItem("token", newToken);
      }
      // Reload to pick up new token and redirect
      window.location.href = "/";
    } catch (err) {
      setError(err.message || "Failed to set password. Please try again.");
      setSettingPassword(false);
    }
  };

  // If still initializing auth state, show loading
  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  const loginLogoUrl = scopedTheme?.login_logo_url || scopedTheme?.logo_url;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4">
      {/* Logo or App Name */}
      <div className="mb-8 text-center">
        {loginLogoUrl && (
          <img
            src={loginLogoUrl}
            alt={scopedTheme?.name || "Organization"}
            className="mx-auto mb-3 h-16 w-auto object-contain"
          />
        )}
        <div className="flex items-center justify-center mb-2">
          <h1 className="text-3xl font-bold org-gradient-text">
            {scopedTheme?.app_name || "AddFlint"}
          </h1>
        </div>
        {scopedTheme?.name && (
          <p className="text-sm text-gray-500">Sign in to {scopedTheme.name}</p>
        )}
      </div>

      {/* Login Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white p-8 rounded-2xl shadow-md w-full max-w-md border border-gray-200"
      >
        <h2 className="text-2xl font-bold mb-6 text-gray-800">Welcome!</h2>

        {/* Success Message */}
        {successMessage && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="mb-6 p-3 bg-green-50 border border-green-200 rounded-2xl flex items-center text-green-700"
          >
            <CheckCircle className="w-5 h-5 mr-2" />
            <p className="text-sm">{successMessage}</p>
          </motion.div>
        )}

        {/* Error Message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="mb-6 p-3 bg-red-50 border border-red-200 rounded-2xl flex items-center text-red-700"
          >
            <AlertCircle className="w-5 h-5 mr-2" />
            <p className="text-sm">{error}</p>
          </motion.div>
        )}

        {/* Step 1: Email */}
        {step === "email" && (
          <form onSubmit={handleCheckEmail} className="space-y-5">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 block">Email Address</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  placeholder="your.email@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={checkingEmail}
              className={`w-full py-3 px-4 rounded-full text-white font-medium flex items-center justify-center transition-all ${
                checkingEmail
                  ? "bg-gray-400"
                  : "org-gradient shadow-md hover:shadow-lg"
              }`}
            >
              {checkingEmail ? (
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <ArrowRight className="w-5 h-5 mr-2" />
              )}
              {checkingEmail ? "Checking..." : "Continue"}
            </button>
          </form>
        )}

        {/* Step 2a: SSO Redirect */}
        {step === "sso-redirect" && (
          <div className="text-center space-y-6">
            <div className="flex justify-center">
              <svg className="animate-spin h-10 w-10 text-purple-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
            <p className="text-gray-600">Redirecting to your organization's sign-in page...</p>
            <button
              type="button"
              onClick={handleBackToEmail}
              className="text-sm font-medium text-purple-600 hover:text-purple-500"
            >
              Cancel
            </button>
          </div>
        )}

        {/* Step 2b: Password */}
        {step === "password" && (
          <form onSubmit={handleLogin} className="space-y-5">
            {/* Email display (read-only) */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 block">Email Address</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  value={email}
                  readOnly
                  className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-full bg-gray-50 text-gray-600"
                />
              </div>
              <button
                type="button"
                onClick={handleBackToEmail}
                className="text-sm font-medium text-purple-600 hover:text-purple-500"
              >
                Use a different email
              </button>
            </div>

            {/* Sign in with Workvivo (SP-initiated SAML) */}
            {workvivoSso.enabled && workvivoSso.orgId && (
              <>
                <button
                  type="button"
                  onClick={handleWorkvivoSso}
                  className="w-full py-3 px-4 rounded-full font-medium flex items-center justify-center transition-all border border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                >
                  <Shield className="w-5 h-5 mr-2" />
                  Sign in with Workvivo
                </button>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-gray-200" />
                  <span className="text-sm text-gray-400">or</span>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>
              </>
            )}

            {/* Password Input */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 block">Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoFocus
                  className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                  Remember me
                </label>
              </div>
              <div className="text-sm">
                <Link to="/password-recovery" className="font-medium text-purple-600 hover:text-purple-500">
                  Forgot your password?
                </Link>
              </div>
            </div>

            {/* Login Button */}
            <button
              type="submit"
              disabled={loading || localLoading}
              className={`w-full py-3 px-4 rounded-full text-white font-medium flex items-center justify-center transition-all ${
                loading || localLoading
                  ? "bg-gray-400"
                  : "org-gradient shadow-md hover:shadow-lg"
              }`}
            >
              {(loading || localLoading) ? (
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <LogIn className="w-5 h-5 mr-2" />
              )}
              {(loading || localLoading) ? "Signing In..." : "Sign In"}
            </button>
          </form>
        )}

        {/* Step 2c: Mixed Choice (SSO + Password) */}
        {step === "mixed-choice" && (
          <div className="space-y-5">
            {/* Email display (read-only) */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 block">Email Address</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  value={email}
                  readOnly
                  className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-full bg-gray-50 text-gray-600"
                />
              </div>
              <button
                type="button"
                onClick={handleBackToEmail}
                className="text-sm font-medium text-purple-600 hover:text-purple-500"
              >
                Use a different email
              </button>
            </div>

            <p className="text-sm text-gray-600 text-center">Choose how you'd like to sign in:</p>

            {/* SSO Button */}
            <button
              type="button"
              onClick={handleMixedChoiceSSO}
              className="w-full py-3 px-4 rounded-full text-white font-medium flex items-center justify-center transition-all org-gradient shadow-md hover:shadow-lg"
            >
              <Shield className="w-5 h-5 mr-2" />
              Sign in with SSO
            </button>

            {/* Sign in with Workvivo (SP-initiated SAML) */}
            {workvivoSso.enabled && workvivoSso.orgId && (
              <button
                type="button"
                onClick={handleWorkvivoSso}
                className="w-full py-3 px-4 rounded-full font-medium flex items-center justify-center transition-all border border-indigo-300 text-indigo-700 hover:bg-indigo-50"
              >
                <Shield className="w-5 h-5 mr-2" />
                Sign in with Workvivo
              </button>
            )}

            {/* Divider */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-sm text-gray-400">or</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>

            {/* Password Button */}
            <button
              type="button"
              onClick={() => setStep("password")}
              className="w-full py-3 px-4 rounded-full font-medium flex items-center justify-center transition-all border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              <Lock className="w-5 h-5 mr-2" />
              Sign in with Password
            </button>
          </div>
        )}

        {/* Step 3: Set Password (forced change for mixed-org first-time password users) */}
        {step === "set-password" && (
          <form onSubmit={handleSetPassword} className="space-y-5">
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-2xl flex items-start text-blue-700">
              <Shield className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
              <p className="text-sm">For security, please set a new password for your account.</p>
            </div>

            {/* New Password */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 block">New Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type={showNewPassword ? "text" : "password"}
                  placeholder="At least 8 characters"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  autoFocus
                  minLength={8}
                  className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 block">Confirm Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type={showNewPassword ? "text" : "password"}
                  placeholder="Confirm your new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={8}
                  className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={settingPassword}
              className={`w-full py-3 px-4 rounded-full text-white font-medium flex items-center justify-center transition-all ${
                settingPassword
                  ? "bg-gray-400"
                  : "org-gradient shadow-md hover:shadow-lg"
              }`}
            >
              {settingPassword ? (
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <CheckCircle className="w-5 h-5 mr-2" />
              )}
              {settingPassword ? "Setting Password..." : "Set Password & Continue"}
            </button>
          </form>
        )}

      </motion.div>

      {/* Footer */}
      <p className="mt-8 text-center text-xs text-gray-500">
        &copy; 2026 AddFlint. All rights reserved.
      </p>
    </div>
  );
};

export default Login;
